import { useCallback } from 'react';

import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from '../../hooks';
import { fetchMenu } from '../menu/menuApi';

import {
  useUpdateMenuMutation,
  useDeleteMenuMutation,
  useAddMenuMutation,
} from './dynamicMenuApi';
import {
  closeDeleteConfirmation,
  openDeleteConfirmation,
  setEditingId,
} from './dynamicMenuSlice';

import type { IAllMenu } from './model';
export const useMenuActions = (allMenu?: IAllMenu[]) => {
  const dispatch = useAppDispatch();
  const editingId = useAppSelector(
    (state) => state.dynamicMenuReducer.editingId
  );
  const deleteConfirmation = useAppSelector(
    (state) => state.dynamicMenuReducer.deleteConfirmation
  );

  const [addMenu] = useAddMenuMutation();
  const [updateMenu] = useUpdateMenuMutation();
  const [deleteMenu] = useDeleteMenuMutation();

  const findMenuById = useCallback(
    (menus: IAllMenu[], id: number | null): IAllMenu | null => {
      if (!menus || !id) {
        return null;
      }

      const findMenu = (menuList: IAllMenu[]): IAllMenu | null => {
        for (const menu of menuList) {
          if (menu.adminMenuId === id) {
            return menu;
          }
          if (menu.subMenu?.length) {
            const found = findMenu(menu.subMenu);
            if (found) {
              return found;
            }
          }
        }
        return null;
      };

      return findMenu(menus);
    },
    []
  );

  const editingMenu = useCallback(() => {
    if (!editingId) {
      return null;
    }

    if (allMenu && allMenu.length > 0) {
      const found = findMenuById(allMenu, editingId);
      if (found) {
        return found;
      }
    }
    return null;
  }, [editingId, allMenu, findMenuById])();

  const handleEditMenu = useCallback(
    (id: number) => {
      dispatch(setEditingId(id));
    },
    [dispatch]
  );

  const handleDeleteMenu = useCallback(async () => {
    if (!deleteConfirmation.id) {
      return;
    }

    try {
      const response = await deleteMenu({ id: deleteConfirmation.id }).unwrap();
      toast.success(response.settings.message);
      dispatch(closeDeleteConfirmation());
      dispatch(fetchMenu());
    } catch (error: unknown) {
      toast.error(error?.data?.settings?.message || 'Delete failed');
      console.error('Delete failed:', error);
    }
  }, [deleteConfirmation, deleteMenu, dispatch]);

  const handleSaveMenu = useCallback(
    async (menuData: IAllMenu) => {
      const isUpdate = !!editingId;
      const isParentMenu = !menuData.parentId || menuData.parentId === 0;

      const payload: Partial<IAllMenu> & { redirectionAllow?: 'Yes' | 'No' } = {
        menuDisplay: menuData.menuDisplay,
        icon: menuData.icon,
        open: menuData.open,
        url: menuData.url ?? '',
        capabilityCode: menuData.capabilityCode ?? null,
        sequenceNumber: menuData.sequenceNumber,
        status: menuData.status,
      };

      if (isParentMenu) {
        payload.redirectionAllow = menuData.url ? 'Yes' : 'No';
      }

      try {
        let response;
        if (isUpdate) {
          response = await updateMenu({
            id: editingId,
            data: { ...payload },
          }).unwrap();
        } else {
          response = await addMenu(payload).unwrap();
        }
        toast.success(response.settings.message);
        dispatch(fetchMenu());
        dispatch(setEditingId(null));
      } catch (error: unknown) {
        toast.error(error?.data?.settings?.message || 'Something went wrong');
      }
    },
    [editingId, updateMenu, addMenu, dispatch]
  );

  return {
    editingMenu,
    editingId,
    deleteConfirmation,
    handleEditMenu,
    handleSaveMenu,
    openDeleteConfirmation: (payload: { id: number; name: string }) =>
      dispatch(openDeleteConfirmation(payload)),
    closeDeleteConfirmation: () => dispatch(closeDeleteConfirmation()),
    handleDeleteMenu,
  };
};
